/**
 * The curation surface: suggested titles as editable rows — remove, reword,
 * add. What the curator saves is exactly what gets created; the suggestion
 * is a starting point, never the decision.
 */
import { useState } from 'react';
import { Button, IconButton, Input } from '@servicecompressionllc/sc-design-system';
import { X } from '@servicecompressionllc/sc-design-system/icons';

export const CurateList = ({
  suggested,
  onSave,
  saving,
  error,
  itemNoun,
}: {
  suggested: string[];
  onSave: (titles: string[]) => void;
  saving: boolean;
  error: Error | null;
  itemNoun: string;
}) => {
  const [rows, setRows] = useState<string[]>(suggested);
  const [added, setAdded] = useState('');
  const clean = rows.map((r) => r.trim()).filter((r) => r.length > 0);

  return (
    <div className="rounded-lg border border-outline bg-surface-elevated p-4">
      <div>
        <p className="text-sm font-medium text-ink">Review the suggested list</p>
        <p className="mt-0.5 text-xs text-ink-muted">
          Remove what doesn’t belong, fix any wording, add what only you would know. Writing starts
          when you keep them — one guide per entry, each arriving as a draft.
        </p>
      </div>
      <ul className="mt-3 space-y-2">
        {rows.map((title, idx) => (
          <li key={idx} className="flex items-center gap-2">
            <span className="w-6 text-right text-xs text-ink-subtle">{idx + 1}.</span>
            <Input
              value={title}
              aria-label={`${itemNoun} ${String(idx + 1)}`}
              onChange={(e) => setRows(rows.map((r, i) => (i === idx ? e.target.value : r)))}
              className="flex-1"
            />
            <IconButton
              aria-label={`Remove ${itemNoun.toLowerCase()} ${String(idx + 1)}`}
              variant="ghost"
              onClick={() => setRows(rows.filter((_, i) => i !== idx))}
            >
              <X size={14} />
            </IconButton>
          </li>
        ))}
      </ul>
      <form
        className="mt-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (added.trim().length > 0) {
            setRows([...rows, added.trim()]);
            setAdded('');
          }
        }}
      >
        <Input
          value={added}
          onChange={(e) => setAdded(e.target.value)}
          placeholder={`Add a ${itemNoun.toLowerCase()} the list missed…`}
          aria-label={`Add a ${itemNoun.toLowerCase()}`}
          className="flex-1"
        />
        <Button type="submit" variant="secondary" disabled={added.trim().length === 0}>
          Add
        </Button>
      </form>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => onSave(clean)} disabled={saving || clean.length === 0}>
          {saving ? 'Saving…' : `Keep these ${String(clean.length)} and start writing`}
        </Button>
        {error && <span className="text-sm text-danger">{error.message}</span>}
      </div>
    </div>
  );
};
